import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { productType, imgUrl } from "../utils/environments/api";

const Case = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("categories"); // "categories" or "products"

  const sectionRef = useRef(null);
  const productRefs = useRef([]);

  const addToProductRefs = (el) => {
    if (el && !productRefs.current.includes(el)) {
      productRefs.current.push(el);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Animation effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: "0px 0px -50px 0px"
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add("animate-in");
      });
    }, observerOptions);

    if (sectionRef.current) observer.observe(sectionRef.current);
    productRefs.current.forEach(ref => ref && observer.observe(ref));

    return () => observer.disconnect();
  }, [filteredProducts, categories]);

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await productType.get(`get_product_category.php`);
      if (res.data.status && res.data.data) {
        setCategories(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch products when category is selected
  const fetchProductsByCategory = async (categoryId, categoryName) => {
    try {
      setLoading(true);
      setSelectedCat(categoryId);
      
      const res = await productType.get(`get_product_typ_api.php?id=${categoryId}`);
      
      if (res.data.status) {
        let productsData = [];
        
        if (Array.isArray(res.data.data)) {
          productsData = res.data.data;
        } else if (res.data.data && typeof res.data.data === 'object') {
          productsData = [res.data.data];
        }
        
        setProducts(productsData);
        
        // Filter products by selected category
        const filtered = productsData.filter(
          (item) => String(item.category_id) === String(categoryId)
        );
        
        setFilteredProducts(filtered);
        setView("products"); // Switch to products view
      }
    } catch (err) {
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  };

  // Go back to categories view
  const handleBackToCategories = () => {
    setView("categories");
    setSelectedCat(null);
  };

  // Loading Skeleton
  if (loading) {
    return (
      <section className="cases-section" ref={sectionRef}>
        <div className="container">
          <div className="row">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <div className="col-md-4 col-sm-12  mb-4" key={item}>
                <div className="product-card loading">
                  <div className="loading-image"></div>
                  <div className="loading-content">
                    <div className="loading-title"></div>
                    <div className="loading-category"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="cases-section" ref={sectionRef}>
      <div className="container">
        {/* Section Header */}
        <div className="section-header centered">
          <div className="subtitle">
            {view === "categories" ? "OUR CATEGORIES" : "PRODUCTS"}
          </div>
          <h2 className="section-title">
            {view === "categories" ? (
              <>Browse <span className="highlight">Product Categories</span></>
            ) : (
              <>Products in <span className="highlight">This Category</span></>
            )}
          </h2>

          {/* Back button when in products view */}
          {view === "products" && (
            <button className="back-to-categories" onClick={handleBackToCategories}>
              <i className="ri-arrow-left-line"></i>
              Back to Categories
            </button>
          )}
        </div>

        {/* CATEGORIES VIEW */}
        {view === "categories" && (
          <div className="row">
            {categories.length > 0 ? (
              categories.map((category, index) => (
                <div 
                  className="col-md-4 col-sm-12 mb-4" 
                  key={category.id}
                >
                  <div
                    className={`category-card category-card-${index % 4 + 1}`}
                    ref={addToProductRefs}
                    onClick={() => fetchProductsByCategory(category.id, category.category_name)}
                  >
                    <div className="card-inner">
                      {/* Image */}
                      <div className="image-container">
                        <div className="image-wrapper">
                          <img
                            src={`${imgUrl}${category.image}`}
                            alt={category.category_name}
                            onError={(e) => {
                              e.target.src = "/api/placeholder/400/300";
                              e.target.alt = "Category Image";
                            }}
                          />
                          <div className="image-overlay"></div>
                          <div className="action-button">
                            <span className="view-products">
                              <i className="ri-arrow-right-up-line"></i>
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="card-content">
                        <div className="category-tag">Category</div>
                        <h3 className="category-title">
                          {category.category_name}
                        </h3>
                        <p className="category-description">
                          {category.content?.substring(0, 80) || `Explore our ${category.category_name} solutions`}
                        </p>

                        <div className="explore-link">
                          <span>View Products</span>
                          <div className="arrow-wrapper">
                            <i className="ri-arrow-right-line"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12">
                <div className="no-categories">No categories available.</div>
              </div>
            )}
          </div>
        )}

        {/* PRODUCTS VIEW */}
        {view === "products" && (
          <div className="row">
            {filteredProducts.length > 0 ? (
              filteredProducts.map((product, index) => (
                <div 
                  className="col-md-4 col-sm-12  mb-4" 
                  key={product.producttype_id}
                >
                  <div
                    className={`product-card product-card-${index % 3 + 1}`}
                    ref={addToProductRefs}
                  >
                    <div className="card-inner">
                      {/* Image */}
                      <div className="image-container">
                        <div className="image-wrapper">
                          <img
                            src={`${imgUrl}${product.image}`}
                            alt={product.product}
                            onError={(e) => {
                              e.target.src = "/api/placeholder/400/300";
                              e.target.alt = "Product Image";
                            }}
                          />
                          <div className="image-overlay"></div>
                          <div className="action-button">
                            <Link
                              to={`/ProductSinglePage?id=${product.producttype_id}`}
                              className="view-details"
                            >
                              <i className="ri-arrow-right-up-line"></i>
                            </Link>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="card-content">
                        <div className="category-tag">Product</div>
                        <h3 className="product-title">
                          <Link to={`/ProductSinglePage?id=${product.producttype_id}`}>
                            {product.product}
                          </Link>
                        </h3>
                        <p className="product-description">
                          {product.content?.substring(0, 80) || "Innovative product solution"}
                        </p>

                        <Link
                          to={`/ProductSinglePage?id=${product.producttype_id}`}
                          className="explore-link"
                        >
                          <span>Explore Product</span>
                          <div className="arrow-wrapper">
                            <i className="ri-arrow-right-line"></i>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12">
                <div className="no-products">
                  <p>No products available in this category.</p>
                  <button className="back-btn" onClick={handleBackToCategories}>
                    Back to Categories
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default Case;